import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '@clerk/react';
import { StatusToast, StatusToastVariant } from '@this+that/ui';
import log from 'loglevel';

import { useCustomToast } from './useCustomToast';
import type { ComposeSnapshot } from '../models/composeSnapshot';
import type { ComposeSnapshotIndexEntry } from '../store/ComposeSnapshotIndex/types';
import { useComposeSnapshotIndexStore } from '../store/ComposeSnapshotIndex/useComposeSnapshotIndexStore';
import { useMessageDialogStore } from '../store/MessageDialog/useMessageDialogStore';
import { listDirtyComposeSnapshots } from '../store/utils/composeSnapshotCache';
import { collectLoadedDraftIds } from '../utils/composeSnapshotSync/sweepOrphans';
import {
  COMPOSE_RECOVERY_TOAST_DURATION_MS,
  COMPOSE_SNAPSHOT_ORPHAN_GRACE_MS,
} from '../utils/constants/drafts';

const LOG_MESSAGES = {
  LISTING_FAILED: 'Unsent compose recovery listing failed',
} as const;

type ToastId = ReturnType<ReturnType<typeof useCustomToast>['customToaster']>;

interface OrphanScope {
  excludedSessionIds: ReadonlySet<string>;
  chiplessOnly: boolean;
}

const hasChip = ({
  draftId,
  sourceMessageId,
}: Pick<ComposeSnapshot, 'draftId' | 'sourceMessageId'>): boolean =>
  Boolean(draftId || sourceMessageId);

// A draftId nothing loaded carries may have been deleted in the provider; the sweep verifies it first.
const isOfferable = (
  { draftId }: Pick<ComposeSnapshot, 'draftId'>,
  verifiedDraftIds: Record<string, true>,
  loadedDraftIds: ReadonlySet<string>,
): boolean =>
  !draftId || verifiedDraftIds[draftId] || loadedDraftIds.has(draftId);

const findOrphans = async (
  userId: string,
  { excludedSessionIds, chiplessOnly }: OrphanScope,
): Promise<ComposeSnapshot[]> => {
  try {
    const snapshots = await listDirtyComposeSnapshots(userId);
    const now = Date.now();
    // The mirror drops a discarded session synchronously, before IndexedDB finishes deleting it.
    const { entriesBySessionId, liveSessionIds, lastLiveAtBySessionId } =
      useComposeSnapshotIndexStore.getState();
    const { verifiedDraftIds } = useComposeSnapshotIndexStore.getState();
    const loadedDraftIds = collectLoadedDraftIds();
    return snapshots.filter(
      (snapshot) =>
        snapshot.sessionId in entriesBySessionId &&
        isOfferable(snapshot, verifiedDraftIds, loadedDraftIds) &&
        !liveSessionIds[snapshot.sessionId] &&
        !excludedSessionIds.has(snapshot.sessionId) &&
        !(chiplessOnly && hasChip(snapshot)) &&
        now -
          Math.max(
            snapshot.updatedAt,
            lastLiveAtBySessionId[snapshot.sessionId] ?? 0,
          ) >=
          COMPOSE_SNAPSHOT_ORPHAN_GRACE_MS,
    );
  } catch (error) {
    log.warn(LOG_MESSAGES.LISTING_FAILED, error);
    return [];
  }
};

// Earliest moment a chip-less, non-live, not-yet-offered entry ages past the grace window.
const findNextOrphanDueAt = (
  entries: Record<string, ComposeSnapshotIndexEntry>,
  liveSessionIds: Record<string, true>,
  lastLiveAtBySessionId: Record<string, number>,
  offeredSessionIds: ReadonlySet<string>,
): number | undefined => {
  const dueTimes = Object.values(entries)
    .filter(
      (entry) =>
        !liveSessionIds[entry.sessionId] &&
        !hasChip(entry) &&
        !offeredSessionIds.has(entry.sessionId),
    )
    .map(
      ({ sessionId, updatedAt }) =>
        Math.max(updatedAt, lastLiveAtBySessionId[sessionId] ?? 0) +
        COMPOSE_SNAPSHOT_ORPHAN_GRACE_MS,
    );
  return dueTimes.length > 0 ? Math.min(...dueTimes) : undefined;
};

const openSnapshot = (
  snapshot: Pick<
    ComposeSnapshotIndexEntry,
    'draftId' | 'sourceMessageId' | 'handler' | 'inboxEntryId' | 'sessionId'
  >,
): void => {
  void useMessageDialogStore.getState().open({
    draftId: snapshot.draftId,
    messageId: snapshot.sourceMessageId,
    selectedHandlerButton: snapshot.handler,
    inboxEntryId: snapshot.inboxEntryId,
    restoreSnapshotSessionId: snapshot.sessionId,
  });
};

const isStillOffered = (
  state: ReturnType<typeof useComposeSnapshotIndexStore.getState>,
  sessionId: string,
): boolean =>
  sessionId in state.entriesBySessionId && !state.liveSessionIds[sessionId];

const findNewestOffered = (
  sessionIds: string[],
): ComposeSnapshotIndexEntry | undefined => {
  const state = useComposeSnapshotIndexStore.getState();
  return sessionIds
    .filter((sessionId) => isStillOffered(state, sessionId))
    .map((sessionId) => state.entriesBySessionId[sessionId])
    .reduce<ComposeSnapshotIndexEntry | undefined>(
      (newest, entry) =>
        !newest || entry.updatedAt > newest.updatedAt ? entry : newest,
      undefined,
    );
};

interface UnsentRecoveryToastProps {
  sessionIds: string[];
  onRecover: (entry: ComposeSnapshotIndexEntry) => void;
  onClose?: () => void;
}

// Follows the index mirror, so the count stays true and the toast leaves once nothing is left.
const UnsentRecoveryToast = ({
  sessionIds,
  onRecover,
  onClose,
}: UnsentRecoveryToastProps) => {
  const { t } = useTranslation();
  const count = useComposeSnapshotIndexStore(
    (state) =>
      sessionIds.filter((sessionId) => isStillOffered(state, sessionId)).length,
  );
  useEffect(() => {
    if (count === 0) {
      onClose?.();
    }
  }, [count, onClose]);

  return (
    <StatusToast
      status={StatusToastVariant.Warning}
      message={t('inbox.messageDialog.unsentRecovery.message', { count })}
      action={{
        label: t('inbox.messageDialog.unsentRecovery.recover'),
        onClick: () => {
          const newest = findNewestOffered(sessionIds);
          if (newest) {
            onRecover(newest);
          }
        },
      }}
      onClose={onClose}
      durationMs={COMPOSE_RECOVERY_TOAST_DURATION_MS}
      dataTestId="unsent-compose-recovery-toast"
    />
  );
};

export const useUnsentComposeRecovery = (): void => {
  const { user } = useUser();
  const userId = user?.id;
  const { customToaster, closeToast } = useCustomToast();
  const isDialogOpen = useMessageDialogStore((state) => state.isOpen);
  const entries = useComposeSnapshotIndexStore(
    (state) => state.entriesBySessionId,
  );
  const liveSessionIds = useComposeSnapshotIndexStore(
    (state) => state.liveSessionIds,
  );

  const showToastRef = useRef(customToaster);
  showToastRef.current = customToaster;
  const closeToastRef = useRef(closeToast);
  closeToastRef.current = closeToast;

  const lastLiveAtBySessionId = useComposeSnapshotIndexStore(
    (state) => state.lastLiveAtBySessionId,
  );
  const verifiedDraftIds = useComposeSnapshotIndexStore(
    (state) => state.verifiedDraftIds,
  );

  const handledVerifiedRef = useRef(verifiedDraftIds);

  const [hasEvaluated, setHasEvaluated] = useState(false);
  const shouldReevaluateRef = useRef(false);
  const recoveredSessionIdsRef = useRef(new Set<string>());
  const offeredSessionIdsRef = useRef(new Set<string>());
  const visibleToastIdRef = useRef<ToastId | undefined>(undefined);
  const offerChainRef = useRef<Promise<void>>(Promise.resolve());

  const recover = useCallback((snapshot: ComposeSnapshotIndexEntry) => {
    recoveredSessionIdsRef.current.add(snapshot.sessionId);
    shouldReevaluateRef.current = true;
    openSnapshot(snapshot);
  }, []);

  const showToast = useCallback(
    (orphans: ComposeSnapshot[]) => {
      if (visibleToastIdRef.current !== undefined) {
        closeToastRef.current(visibleToastIdRef.current);
      }
      visibleToastIdRef.current = showToastRef.current(
        <UnsentRecoveryToast
          sessionIds={orphans.map(({ sessionId }) => sessionId)}
          onRecover={recover}
        />,
        undefined,
        COMPOSE_RECOVERY_TOAST_DURATION_MS,
      );
    },
    [recover],
  );

  const offerRecovery = useCallback(
    async (
      currentUserId: string,
      chiplessOnly: boolean,
      excludeOffered = chiplessOnly,
    ) => {
      const orphans = await findOrphans(currentUserId, {
        chiplessOnly,
        excludedSessionIds: excludeOffered
          ? new Set([
              ...recoveredSessionIdsRef.current,
              ...offeredSessionIdsRef.current,
            ])
          : recoveredSessionIdsRef.current,
      });
      if (orphans.length === 0) {
        return;
      }
      orphans.forEach(({ sessionId }) =>
        offeredSessionIdsRef.current.add(sessionId),
      );
      showToast(orphans);
    },
    [showToast],
  );

  // Offers are serialized so a mid-session pass sees what the load pass already offered.
  const enqueueOffer = useCallback(
    (
      currentUserId: string,
      chiplessOnly: boolean,
      excludeOffered?: boolean,
    ) => {
      offerChainRef.current = offerChainRef.current.then(() =>
        offerRecovery(currentUserId, chiplessOnly, excludeOffered),
      );
    },
    [offerRecovery],
  );

  useEffect(() => {
    if (!userId || isDialogOpen) {
      return;
    }
    if (hasEvaluated && !shouldReevaluateRef.current) {
      return;
    }
    shouldReevaluateRef.current = false;
    setHasEvaluated(true);
    enqueueOffer(userId, false);
  }, [userId, isDialogOpen, hasEvaluated, enqueueOffer]);

  // The sweep verified a draftId after the load pass skipped its snapshot: offer it now.
  useEffect(() => {
    if (
      !userId ||
      isDialogOpen ||
      !hasEvaluated ||
      handledVerifiedRef.current === verifiedDraftIds
    ) {
      return;
    }
    handledVerifiedRef.current = verifiedDraftIds;
    enqueueOffer(userId, false, true);
  }, [userId, isDialogOpen, hasEvaluated, verifiedDraftIds, enqueueOffer]);

  useEffect(() => {
    if (!userId || isDialogOpen || !hasEvaluated) {
      return;
    }
    const dueAt = findNextOrphanDueAt(
      entries,
      liveSessionIds,
      lastLiveAtBySessionId,
      offeredSessionIdsRef.current,
    );
    if (dueAt === undefined) {
      return;
    }
    const timer = setTimeout(
      () => enqueueOffer(userId, true),
      Math.max(0, dueAt - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [
    userId,
    isDialogOpen,
    hasEvaluated,
    entries,
    liveSessionIds,
    lastLiveAtBySessionId,
    enqueueOffer,
  ]);
};
